/**
 * A partner's free-waiting terms, as the dashboard reads and writes them.
 *
 * THE NUMBERS ARE NEVER HELD HERE. The API resolves them (ph-core
 * freeWaiting.helper) and sends them on every partner row as
 * `free_waiting_policy`, so the three screens that name them — the Waiting
 * Times modal, the warning when a partner is put on a booking, and the flag
 * on the partners list — cannot drift from the countdown the driver's phone
 * actually runs, nor from each other, the first time the office
 * renegotiates.
 *
 * Where no policy came back (an older API answering a newer screen) every
 * reader here answers "we do not know" and the screens print no numbers
 * rather than a guess dressed as a fact.
 */

/** The three boxes, in the order the office thinks about them. */
export const WAITING_TIME_FIELDS = [
  {
    field: 'free_waiting_airport_minutes',
    // The matching key in `free_waiting_policy`, for the default and the
    // value in force.
    policyKey: 'airport_minutes',
    label: 'Airport pickup',
    hint: 'How long the driver waits free of charge when the PICKUP is at an airport — '
      + 'bags, passport queues, a slow carousel.',
  },
  {
    field: 'free_waiting_standard_minutes',
    policyKey: 'standard_minutes',
    label: 'Everywhere else',
    hint: 'The same, for any other pickup — a hotel lobby, an address.',
  },
  {
    field: 'free_waiting_max_flight_delay_minutes',
    policyKey: 'max_flight_delay_minutes',
    label: 'Most flight delay absorbed',
    hint: 'A late arriving flight pushes the whole window back by the delay, up to this cap. '
      + 'Past it the driver is told to check with the office rather than keep waiting.',
  },
];

/** The resolved policy the API sent with this partner, or null. */
export const waitingTimesPolicy = (partner) => partner?.free_waiting_policy || null;

/** The house numbers an empty box falls back to, or null when the API said nothing. */
export const houseWaitingTimes = (partner) => waitingTimesPolicy(partner)?.house || null;

/** Has anybody agreed terms with this channel? */
export const hasWaitingTimes = (partner) => waitingTimesPolicy(partner)?.configured === true;

/**
 * Should a screen warn about this partner?
 *
 * Only for a partner we KNOW has no terms. A booking with no partner at all
 * is PH-direct, where the house numbers are the right answer and nagging
 * would be noise; and a partner whose policy never arrived is not evidence
 * of anything.
 */
export const needsWaitingTimes = (partner) => Boolean(partner)
  && waitingTimesPolicy(partner)?.configured === false;

/**
 * The three boxes as the API takes them.
 *
 * An empty box is `null`, which clears the term and puts the channel back on
 * the house numbers — NOT 0, which the API reads as a real agreement to give
 * no free waiting at all. `Number('')` is 0, so this cannot be left to the
 * plain numeric cast the other fields use.
 */
export const waitingTimesPayload = (partner) => Object.fromEntries(
  WAITING_TIME_FIELDS.map(({ field }) => {
    const raw = partner?.[field];
    const value = raw === '' || raw === null || raw === undefined ? null : Number(raw);
    return [field, Number.isFinite(value) ? value : null];
  }),
);

/**
 * What a screen should say about a partner with no terms: what will actually
 * happen, not merely that a box is empty. Null when there is nothing to warn
 * about, or when we do not know the numbers to name.
 */
export const waitingTimesWarning = (partner) => {
  if (!needsWaitingTimes(partner)) return null;

  const house = houseWaitingTimes(partner);
  if (!house) return null;

  return `No waiting times agreed for ${partner.name || 'this partner'} — this booking will run on `
    + `the house defaults: ${house.airport_minutes} min at an airport, `
    + `${house.standard_minutes} min elsewhere, and up to ${house.max_flight_delay_minutes} min `
    + 'of flight delay absorbed.';
};
